/* globals fetch, localStorage */
// 2025 update to use new getRequestOptions
import Globals from "../utils/globals";
import { logException } from "../utils/AppLogger";
import { getRequestOptions } from "../utils/ApiHelper";
import { setError } from "./findError";

export const REQUEST_REPORT_CSV = "REQUEST_REPORT_CSV";
export const RECEIVE_REPORT_CSV = "RECEIVE_REPORT_CSV";
export const REQUEST_REPORT_FAILURE_CSV = "REQUEST_REPORT_FAILURE_CSV";
export const CLEAR_IS_FETCHING = "CLEAR_IS_FETCHING";
// export const SET_REPORT_VISIBILITY = 'SET_REPORT_VISIBILITY'

/**
 * @author Sultan
 * 03-03-2023
 * create csv action called csvReport.
 */
export const requestReport = (id) => {
  return {
    type: REQUEST_REPORT_CSV,
    agencyId: id,
  };
};

export const clearIsfetching = () => {
  return {
    type: CLEAR_IS_FETCHING,
  };
};

export const receiveReport = (json) => {
  return {
    type: RECEIVE_REPORT_CSV,
    items: json.items,
    totalPage: json.totalPages,
    languages: json.languages,
    current_language: json.current_language,
    receivedAt: Date.now(),
  };
};

export const fetchReportFailure = (error) => {
  logException(error, {
    profile: localStorage.getItem("profile"),
  });

  return {
    type: REQUEST_REPORT_FAILURE_CSV,
    error,
  };
};

export const fetchReportCSV = (id, token, params = {}) => {
  console.log("params csv", params);
  return (dispatch) => {
    let res;
    let fetchUrl;

    if (!token) {
      console.error('csvReports.js - No token provided');
      return dispatch(
        setError(
          null,
          "Token not found",
          "No API token available. Please log in again."
        )
      );
    }

    fetchUrl = `${Globals.apiEndpoint}/dashboard/agencies/${id}/reporting`;
    if (Object.keys(params).length) {
      fetchUrl +=
        "?" +
        Object.keys(params)
          .map((queryParam) => `${queryParam}=${params[queryParam]}`)
          .join("&");
    }

    const requestOptions = getRequestOptions(token);

    dispatch(requestReport(id));
    return fetch(fetchUrl, requestOptions)
      .then((response) => {
        res = response;
        return response.json();
      })
      .then((json) => {
        if (res.status !== 200) {
          return dispatch(setError(res.status, res.statusText, json.message));
        } else {
          dispatch(receiveReport(json));
        }
      })
      .catch((error) => {
        dispatch(fetchReportFailure(error));
        dispatch(setError(null, "Network Error", error.message));
      });
  };
};

/**
 * @author xt
 * 10-03-2026
 *
 * REPORTS TAB - EXPORT ALL MATCHING REPORTS
 *
 * Retrieves the complete filtered Reports export in manageable batches.
 *
 * The normal Reports screen is paginated and only requests the current
 * page of records. The legacy fetchReportCSV() action makes one unpaginated
 * request and is still used by other Console areas such as MapTab, so it is
 * intentionally left unchanged.
 *
 * For large agencies, requesting every matching report in one call can
 * require Mongo/Node/Heroku to build and return thousands of reports at
 * once. This export instead uses the existing /reporting pagination support
 * and requests 500 (hard-coded here) records at a time.
 *
 * After every page has been received, the batches are combined and
 * receiveReport() is dispatched ONCE. This preserves the existing Redux
 * state and CSV formatting behavior used by the Reports tab.
 */
export const fetchAllReportCSV = (id, token, params = {}) => {
  const EXPORT_BATCH_SIZE = 500;

  return async (dispatch) => {
    if (!token) {
      console.error("csvReports.js - No token provided for Reports export");

      return dispatch(
        setError(
          null,
          "Token not found",
          "No API token available. Please log in again."
        )
      );
    }

    dispatch(requestReport(id));

    try {
      const requestOptions = getRequestOptions(token);

      let page = 1;
      let totalPages = 1;
      let allItems = [];

      // Preserve metadata returned by the /reporting endpoint so the final
      // Redux payload has the same shape as the legacy CSV request.
      let languages = [];
      let currentLanguage = null;

      do {
        const batchParams = {
          ...params,
          page,
          pageSize: EXPORT_BATCH_SIZE,
        };

        const queryString = Object.keys(batchParams)
          .map(
            (queryParam) =>
              `${queryParam}=${batchParams[queryParam]}`
          )
          .join("&");

        const fetchUrl =
          `${Globals.apiEndpoint}/dashboard/agencies/${id}/reporting` +
          `?${queryString}`;

        console.log(
          page === 1
            ? "[Reports Export] Fetching first batch"
            : `[Reports Export] Fetching batch ${page} of ${totalPages}`
        );

        const response = await fetch(fetchUrl, requestOptions);
        const json = await response.json();

        if (!response.ok) {
          throw new Error(
            json?.message ||
              `Reports export failed with status ${response.status}`
          );
        }

        const batchItems = Array.isArray(json?.items)
          ? json.items
          : [];

        allItems = allItems.concat(batchItems);

        // The backend calculates totalPages from count / pageSize whenever
        // page and pageSize are supplied.
        totalPages =
          Number(json?.totalPages) > 0
            ? Number(json.totalPages)
            : 1;

        if (page === 1) {
          languages = json?.languages || [];
          currentLanguage = json?.current_language || null;
        }

        console.log(
          `[Reports Export] Received ${batchItems.length} records; ` +
            `${allItems.length} total so far`
        );

        page += 1;
      } while (page <= totalPages);

      console.log(
        `[Reports Export] Complete: ${allItems.length} records`
      );

      dispatch(
        receiveReport({
          items: allItems,
          totalPages,
          languages,
          current_language: currentLanguage,
        })
      );
    } catch (error) {
      console.error(
        "csvReports.js - Reports export failed:",
        error
      );

      dispatch(fetchReportFailure(error));
      dispatch(
        setError(
          null,
          "Export Error",
          error.message
        )
      );
    }
  };
};
